import { useMemo, useState } from "react";
import { History, Play, X } from "lucide-react";
import { Button } from "~/components/ui/button";
import { MultiSelect } from "~/components/ui/multi-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import useNextGenHeader from "~/contexts/nextgen-header";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import { AdminPage } from "~/pages/manifold-nextgen/components/admin/AdminPage";
import { useCanManageAIAgents } from "~/pages/manifold-nextgen/hooks/useCanManageAIAgents";
import { nextgenAdminEvalsPath } from "~/pages/manifold-nextgen/paths";
import type { EvalRunListItem } from "~/services/evals";
import { DeleteRunDialog } from "./DeleteRunDialog";
import { TableSkeleton } from "./EvalSkeletons";
import { LaunchRunDialog } from "./LaunchRunDialog";
import { RunDetailSheet } from "./RunDetailSheet";
import { RunsListTable } from "./RunsListTable";
import { MATRIX_RUN_COLUMNS } from "./constants/page";
import { ALL_FILTER, RUN_STATUS_FILTER_OPTIONS } from "./constants/runs";
import { ShownCount, TruncationNotice } from "./primitives/notices";
import { collectAgentIds } from "./selectors/facets";
import {
  UNCONFIGURED_CONFIG_KEY,
  buildRunRows,
  groupRunsByConfig,
  runConfigKey,
} from "./selectors/runs";
import {
  sortRunsChronologically,
  useEvalCases,
  useEvalRuns,
  useResultsForRuns,
  useRunAgentLabels,
} from "./useEvalApi";
import { useEvalFilters } from "./useEvalFilters";

/** Roughly the runs table's columns, for the skeleton. */
const RUN_COLUMN_WIDTHS = ["w-24", "w-20", "w-32", "w-24", "w-16", "w-20", "w-12", "w-16", "w-16"];

/**
 * Every eval run on its own page, newest first. The run itself opens in a drawer
 * over the list rather than a page of its own, so a reviewer scanning for the run
 * that regressed keeps their place.
 */
const EvalRunsPage = () => {
  useDocumentTitle("Eval runs");

  const filters = useEvalFilters();
  const canDelete = useCanManageAIAgents();
  const [launchOpen, setLaunchOpen] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [runPendingDelete, setRunPendingDelete] = useState<EvalRunListItem | null>(null);

  // Memoised: the header compares its actions deeply, and an inline closure is a
  // new value every render, which would set header state on each one.
  const headerActions = useMemo<[React.ReactNode]>(
    () => [
      <Button key="launch-run" size="sm" onClick={() => setLaunchOpen(true)}>
        <Play className="size-4" aria-hidden />
        Launch run
      </Button>,
    ],
    []
  );
  useNextGenHeader({ actions: headerActions });

  const { data: runsPage, isLoading: runsLoading, mutate: refetchRuns } = useEvalRuns();
  const { data: casesPage } = useEvalCases();

  const allRuns = runsPage?.items ?? [];
  const agentLabels = useRunAgentLabels(allRuns.map((run) => run.agent_id));

  // Status, agent and config are applied here, as on the dashboard: the run list
  // route filters only by tag. Config options come from the whole run set so they
  // do not shrink as the other facets narrow the runs.
  const { groups: configGroups, unconfiguredCount: unconfiguredConfigCount } =
    groupRunsByConfig(allRuns);
  const filteredRuns = allRuns.filter(
    (run) =>
      (filters.status === ALL_FILTER || run.status === filters.status) &&
      (filters.agents.length === 0 ||
        (run.agent_id != null && filters.agents.includes(run.agent_id))) &&
      (filters.config === ALL_FILTER || runConfigKey(run) === filters.config)
  );

  // A run's outcome columns come from its results, a request per run, so only the
  // most recent runs are measured — the same window the dashboard uses.
  const windowRuns = sortRunsChronologically(filteredRuns).slice(-MATRIX_RUN_COLUMNS);
  const { data: resultsBundle } = useResultsForRuns(windowRuns.map((run) => run.id));
  const resultsPending = windowRuns.length > 0 && resultsBundle == null;
  const rows = buildRunRows(filteredRuns, resultsBundle?.resultsByRun ?? {});

  const agentOptions = collectAgentIds(allRuns).map((agentId) => ({
    value: agentId,
    label: agentLabels.agentLabel(agentId) ?? agentId,
  }));

  return (
    <>
      <AdminPage
        title="Runs"
        description="Every evaluation of one or more cases, newest first."
        back={{ href: nextgenAdminEvalsPath(), label: "Back to Evals", variant: "text" }}
        width="wide"
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <MultiSelect
              options={agentOptions}
              value={filters.agents}
              onValueChange={filters.setAgents}
              placeholder="All agents"
              className="w-[170px]"
            />

            <Select value={filters.status} onValueChange={filters.setStatus}>
              <SelectTrigger className="w-[150px]" aria-label="Filter runs by status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RUN_STATUS_FILTER_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Runs are comparable only within one judge configuration, so this
                isolates one. Shown once there is a choice to make, or whenever a
                config filter is already set, so it stays clearable. */}
            {(configGroups.length + (unconfiguredConfigCount > 0 ? 1 : 0) > 1 ||
              filters.config !== ALL_FILTER) && (
              <Select value={filters.config} onValueChange={filters.setConfig}>
                <SelectTrigger className="w-[180px]" aria-label="Filter runs by judging config">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_FILTER}>All judging configs</SelectItem>
                  {configGroups.map((group) => (
                    <SelectItem key={group.key} value={group.key}>
                      Judging config {group.index}
                    </SelectItem>
                  ))}
                  {unconfiguredConfigCount > 0 && (
                    <SelectItem value={UNCONFIGURED_CONFIG_KEY}>Not recorded</SelectItem>
                  )}
                </SelectContent>
              </Select>
            )}

            {filters.hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={filters.clearFilters}>
                <X className="size-4" aria-hidden />
                Clear
              </Button>
            )}

            <ShownCount shown={rows.length} total={allRuns.length} noun="run" />
          </div>

          <TruncationNotice
            truncations={
              runsPage?.truncated
                ? [{ noun: "runs", shown: allRuns.length, total: runsPage.totalCount }]
                : []
            }
          />

          <div className="overflow-auto rounded-lg border">
            {runsLoading && rows.length === 0 ? (
              <TableSkeleton columns={RUN_COLUMN_WIDTHS} />
            ) : rows.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-12 text-center">
                <History className="size-8 text-muted-foreground/60" aria-hidden />
                <p className="text-sm font-medium text-foreground">
                  No eval runs match this filter
                </p>
                <p className="max-w-md text-sm text-muted-foreground">
                  Runs arrive from the nightly schedule, from CI, or from a launch here.
                </p>
              </div>
            ) : (
              <RunsListTable
                rows={rows}
                agentLabels={agentLabels}
                resultsLoading={resultsPending}
                onSelectRun={setSelectedRunId}
                onDeleteRun={canDelete ? setRunPendingDelete : null}
              />
            )}
          </div>
        </div>
      </AdminPage>

      {/* Mounted only while open, so the form resets between launches. */}
      {launchOpen && (
        <LaunchRunDialog
          onOpenChange={setLaunchOpen}
          cases={casesPage?.items ?? []}
          initialTags={[]}
          onLaunched={() => void refetchRuns()}
        />
      )}

      <RunDetailSheet
        open={selectedRunId != null}
        onOpenChange={(open) => !open && setSelectedRunId(null)}
        runId={selectedRunId}
        onDeleted={() => void refetchRuns()}
      />

      <DeleteRunDialog
        run={runPendingDelete}
        onOpenChange={(open) => !open && setRunPendingDelete(null)}
        onDeleted={() => void refetchRuns()}
      />
    </>
  );
};

export default EvalRunsPage;
